import { generateClient } from 'aws-amplify/data'
import { type Schema } from '../../amplify/data/resource'
import { fetchAuthSession } from 'aws-amplify/auth'

const client = generateClient<Schema>()

export interface ContentAssignment {
  id: string
  userId: string
  contentId: string
  targetType: 'device' | 'group'
  targetId: string
  assignedAt?: string
  status?: 'assigned' | 'syncing' | 'synced' | 'failed'
  createdAt?: string
  updatedAt?: string
}

export interface AssignContentRequest {
  contentId: string
  targetType: 'device' | 'group'
  targetIds: string[]
}

class ContentAssignmentService {
  /**
   * Assign content to devices or groups
   */
  async assignContent(request: AssignContentRequest): Promise<ContentAssignment[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const assignments: ContentAssignment[] = []

      for (const targetId of request.targetIds) {
        const { data: assignment, errors } = await client.models.ContentAssignment.create({
          userId,
          contentId: request.contentId,
          targetType: request.targetType,
          targetId,
          assignedAt: new Date().toISOString(),
          status: 'assigned',
        })

        if (errors) {
          console.error('Error creating content assignment:', errors)
          throw new Error('Failed to create content assignment')
        }

        assignments.push({
          id: assignment?.id ?? '',
          userId: assignment?.userId ?? '',
          contentId: assignment?.contentId ?? '',
          targetType: (assignment?.targetType as 'device' | 'group') ?? 'device',
          targetId: assignment?.targetId ?? '',
          assignedAt: assignment?.assignedAt ?? undefined,
          status: (assignment?.status as 'assigned' | 'syncing' | 'synced' | 'failed') ?? 'assigned',
          createdAt: assignment?.createdAt ?? undefined,
          updatedAt: assignment?.updatedAt ?? undefined,
        })
      }

      return assignments
    } catch (error) {
      console.error('Error in assignContent:', error)
      throw error
    }
  }

  /**
   * Get content assignments for a specific content item
   */
  async getContentAssignments(contentId: string): Promise<ContentAssignment[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: assignments, errors } = await client.models.ContentAssignment.listContentAssignmentByContentIdAndAssignedAt({
        contentId,
      })

      if (errors) {
        console.error('Error fetching content assignments:', errors)
        throw new Error('Failed to fetch content assignments')
      }

      return assignments
        .filter(assignment => assignment?.userId === userId)
        .map(assignment => ({
          id: assignment?.id ?? '',
          userId: assignment?.userId ?? '',
          contentId: assignment?.contentId ?? '',
          targetType: (assignment?.targetType as 'device' | 'group') ?? 'device',
          targetId: assignment?.targetId ?? '',
          assignedAt: assignment?.assignedAt ?? undefined,
          status: (assignment?.status as 'assigned' | 'syncing' | 'synced' | 'failed') ?? 'assigned',
          createdAt: assignment?.createdAt ?? undefined,
          updatedAt: assignment?.updatedAt ?? undefined,
        }))
    } catch (error) {
      console.error('Error in getContentAssignments:', error)
      throw error
    }
  }

  /**
   * Get all assignments for a device
   */
  async getDeviceAssignments(deviceId: string): Promise<ContentAssignment[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: assignments, errors } = await client.models.ContentAssignment.listContentAssignmentByTargetIdAndAssignedAt({
        targetId: deviceId,
      })

      if (errors) {
        console.error('Error fetching device assignments:', errors)
        throw new Error('Failed to fetch device assignments')
      }

      return assignments
        .filter(assignment => assignment?.userId === userId && assignment?.targetType === 'device')
        .map(assignment => ({
          id: assignment?.id ?? '',
          userId: assignment?.userId ?? '',
          contentId: assignment?.contentId ?? '',
          targetType: (assignment?.targetType as 'device' | 'group') ?? 'device',
          targetId: assignment?.targetId ?? '',
          assignedAt: assignment?.assignedAt ?? undefined,
          status: (assignment?.status as 'assigned' | 'syncing' | 'synced' | 'failed') ?? 'assigned',
          createdAt: assignment?.createdAt ?? undefined,
          updatedAt: assignment?.updatedAt ?? undefined,
        }))
    } catch (error) {
      console.error('Error in getDeviceAssignments:', error)
      throw error
    }
  }

  /**
   * Remove content assignment
   */
  async removeAssignment(assignmentId: string): Promise<void> {
    try {
      const { errors } = await client.models.ContentAssignment.delete({
        id: assignmentId,
      })

      if (errors) {
        console.error('Error removing content assignment:', errors)
        throw new Error('Failed to remove content assignment')
      }
    } catch (error) {
      console.error('Error in removeAssignment:', error)
      throw error
    }
  }

  /**
   * Update assignment status
   */
  async updateAssignmentStatus(assignmentId: string, status: 'assigned' | 'syncing' | 'synced' | 'failed'): Promise<ContentAssignment> {
    try {
      const { data: assignment, errors } = await client.models.ContentAssignment.update({
        id: assignmentId,
        status,
      })

      if (errors) {
        console.error('Error updating assignment status:', errors)
        throw new Error('Failed to update assignment status')
      }

      return {
        id: assignment?.id ?? '',
        userId: assignment?.userId ?? '',
        contentId: assignment?.contentId ?? '',
        targetType: (assignment?.targetType as 'device' | 'group') ?? 'device',
        targetId: assignment?.targetId ?? '',
        assignedAt: assignment?.assignedAt ?? undefined,
        status: (assignment?.status as 'assigned' | 'syncing' | 'synced' | 'failed') ?? 'assigned',
        createdAt: assignment?.createdAt ?? undefined,
        updatedAt: assignment?.updatedAt ?? undefined,
      }
    } catch (error) {
      console.error('Error in updateAssignmentStatus:', error)
      throw error
    }
  }

  /**
   * Remove content from specific devices
   * This removes the assignment so the device will delete the local content on next sync
   * The content remains in S3 for future assignments
   */
  async removeContentFromDevices(contentId: string, deviceIds: string[]): Promise<void> {
    try {
      // Get all assignments for this content
      const assignments = await this.getContentAssignments(contentId)
      
      // Filter to only the specified devices
      const assignmentsToRemove = assignments.filter(
        assignment => assignment.targetType === 'device' && deviceIds.includes(assignment.targetId)
      )

      // Remove each assignment
      await Promise.all(
        assignmentsToRemove.map(assignment => this.removeAssignment(assignment.id))
      )

      console.log(`Removed ${assignmentsToRemove.length} content assignments from devices`)
    } catch (error) {
      console.error('Error in removeContentFromDevices:', error)
      throw error
    }
  }

  /**
   * Remove content from a specific device
   */
  async removeContentFromDevice(contentId: string, deviceId: string): Promise<void> {
    try {
      await this.removeContentFromDevices(contentId, [deviceId])
    } catch (error) {
      console.error('Error in removeContentFromDevice:', error)
      throw error
    }
  }

  /**
   * Get all content assigned to a device (for device view)
   */
  async getDeviceContent(deviceId: string): Promise<string[]> {
    try {
      const assignments = await this.getDeviceAssignments(deviceId)
      return assignments.map(assignment => assignment.contentId)
    } catch (error) {
      console.error('Error in getDeviceContent:', error)
      throw error
    }
  }
}

export const contentAssignmentService = new ContentAssignmentService()
export default contentAssignmentService